import { ITransportationTypeCreateDTO, ITransportationTypeDetailDTO, ITransportationTypeUpdateBaseDTO, ITransportationTypeUpdateDTO, TransportationTypeCreateDTO, TransportationTypesClient, TransportationTypeUpdateDTO } from 'api/swagger/ApiClient';
import { autoinject, computedFrom } from 'aurelia-framework';
import { Redirect, RouteConfig, Router } from 'aurelia-router';
import { DialogHelper } from 'framework/aurelia_dialogs/dialog-helper';
import { EventHelper } from 'framework/aurelia_tools/event-helper';
import { log } from 'framework/script_utilities/dev-decorators';
import { uuidv4 } from 'framework/script_utilities/uuid-generator';
import { TransportationTypeModel } from 'model/forms/transportation-type-model';
import { EnumsService } from 'services/enums-service';
import { requireServices } from 'support/activation';
import { HOME_ROUTE } from 'support/settings';
import { ToastFormat, ToastHelper, ToastType } from 'support/toaster';
import { ValidationHelper } from 'support/validation';

@autoinject
export class AdminTransportationTypesScreen {
  route: RouteConfig = null;
  items: TransportationTypeModel[] = [];
  creatingNew = false;
  loaded = false;
  handlerClass = `__handler-${uuidv4()}`;

  constructor(private api: TransportationTypesClient, private enums: EnumsService, private router: Router, private dialogHelper: DialogHelper, private toastHelper: ToastHelper, private validationHelper: ValidationHelper, private eventHelper: EventHelper) {
  }

  async canActivate() {
    await requireServices(this.enums);
  }

  @log('debug')
  async activate(params, route: RouteConfig) {
    this.route = route;
  }

  bind() {
    this.initialize();
  }

  private async initialize() {
    this.bindScreen(await this.api.getAll());
    this.loaded = true;
  }

  async canDeactivate() {
    if (this.items.some(x => x.hasChanged) && !await this.dialogHelper.confirm({ dialogTitle: 'Leave without saving?', message: 'You haven\'t saved the form yet. If you proceed, your changes will be lost!', okTitle: 'Leave without saving', cancelTitle: 'Stay here!' }))
      return new Redirect((this.router.history as any).previousLocation, { trigger: false, replace: false });
  }

  createNew() {
    if (!this.items.some(x => x.data.id == null)) {
      this.items.unshift({
        data: {
          name: null,
          icon: null,
          transportationSystemType: null,
          archivedBy: null,
          archivedOn: null,
          isActive: true
        } as ITransportationTypeDetailDTO,
        hasChanged: true,
        validationController: null
      } as TransportationTypeModel)
      this.creatingNew = true;
    }
  }

  cancelCreateNew() {
    const newItemIndex = this.items.findIndex(x => x.data.id == null);
    if (newItemIndex > -1)
      this.items.splice(newItemIndex, 1);
    this.creatingNew = false;
  }

  async saveItem(item: TransportationTypeModel) {
    await this.validationHelper.validate(item.validationController) && await this.save(item);
  }

  async cancelItemChanges(item: TransportationTypeModel) {
    if (!item.hasChanged || await this.dialogHelper.confirm({ dialogTitle: 'Cancel your changes?', message: 'You haven\'t saved the form yet. If you proceed, your changes will be lost!', okTitle: 'Sure, do it!', cancelTitle: 'Back to form' })) {
      if (item.data.id) {
        item.cancelChanges();
      }
      else {
        this.cancelCreateNew();
      }
    }
  }

  async archiveItem(item: TransportationTypeModel) {
    item.rebindData(await this.api.archiveTransportationType(item.data.id));
    this.eventHelper.publish('settings-changed');
    this.toastHelper.toast('Transportation Type has been successfully <b>archived</b>.', 'Archived', ToastType.Success, ToastFormat.Html);
  }

  async restoreItem(item: TransportationTypeModel) {
    item.rebindData(await this.api.unarchiveTransportationType(item.data.id));
    this.eventHelper.publish('settings-changed');
    this.toastHelper.toast('Transportation Type has been successfully <b>restored</b>.', 'Restored', ToastType.Success, ToastFormat.Html);
  }

  async orderChanged() {
    await this.api.setTransportationTypesOrder(this.items.map(x => x.data.id));
    this.toastHelper.toast('Transportation Types order has been successfully <b>changed</b>.', 'Order changed', ToastType.Success, ToastFormat.Html);
  }

  async reorderItem(item: TransportationTypeModel, index: number) {
    this.items.splice(index, 0, ...this.items.splice(this.items.indexOf(item), 1));
    await this.api.setTransportationTypesOrder(this.items.map(x => x.data.id));
    this.toastHelper.toast('Transportation Types order has been successfully <b>changed</b>.', 'Order changed', ToastType.Success, ToastFormat.Html);
  }

  @log('debug')
  private bindScreen(dtos: ITransportationTypeDetailDTO[]) {
    this.items = dtos.map(dto => ({
      data: dto,
      hasChanged: false,
      validationController: null
    } as TransportationTypeModel
    ))
  }

  private async save(item: TransportationTypeModel) {
    const data = this.getSaveData(item.data);
    let dto = null as ITransportationTypeDetailDTO;
    if (item.data.id)
      dto = await this.api.updateTransportationType(item.data.id, new TransportationTypeUpdateDTO(data as ITransportationTypeUpdateDTO));
    else {
      dto = await this.api.createTransportationType(new TransportationTypeCreateDTO(data as ITransportationTypeCreateDTO))
      this.creatingNew = false;
    }
    item.rebindData(dto);
    this.eventHelper.publish('settings-changed');
    this.toastHelper.toast('Transportation Type has been successfully <b>saved</b>.', 'Saved', ToastType.Success, ToastFormat.Html);
  }

  getSaveData(model: ITransportationTypeDetailDTO): ITransportationTypeUpdateBaseDTO {
    return {
      name: model.name,
      icon: model.icon,
      transportationSystemType: model.transportationSystemType.value
    }
  }

  get homeRoute() {
    return HOME_ROUTE;
  }

  get title() {
    return 'Transportation Types';
  }

  @computedFrom('handlerClass')
  get handlerSelector() {
    return `.${this.handlerClass}`;
  }
}
